import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../../constants/apiConfig';

export type IcsIncidentOrganizationAssignment = {
  id?: UUID;
  incident_id: UUID;
  position_key: string;
  name?: string | null;
  version?: number;
};

export const keys = {
  base: 'icsIncidentOrganizationAssignments',
  byIncident: (incidentId: UUID) => [keys.base, incidentId],
} as const;

export const useIcsIncidentOrganizationAssignments = (incidentId: string) => {
  return useQuery({
    queryKey: keys.byIncident(incidentId),
    queryFn: async () => {
      const resp = await axios.get<IcsIncidentOrganizationAssignment[]>(
        `${config.apiGateway.ics_incident_organization_assignments}/${incidentId}`
      );
      return resp.data;
    },
    enabled: !!incidentId,
    throwOnError: () => false,
  });
};

export const useUpsertIcsIncidentOrganizationAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (assignment: IcsIncidentOrganizationAssignment) => {
      const resp = await axios.post<IcsIncidentOrganizationAssignment>(
        `${config.apiGateway.ics_incident_organization_assignments}`,
        assignment
      );
      return resp.data;
    },
    onMutate: async (variables) => {
      const queryKey = keys.byIncident(variables.incident_id);
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<IcsIncidentOrganizationAssignment[]>(queryKey);
      queryClient.setQueryData<IcsIncidentOrganizationAssignment[]>(
        queryKey,
        (old) => {
          const list = old ?? [];
          const idx = list.findIndex(
            (a) => a.position_key === variables.position_key
          );
          if (idx === -1) return [...list, variables];
          const next = list.slice();
          next[idx] = { ...list[idx], ...variables };
          return next;
        }
      );
      return { previous };
    },
    onError: (error: any, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          keys.byIncident(variables.incident_id),
          context.previous
        );
      }
      const message =
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save organization assignment';
      toast.error(message);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: keys.byIncident(variables.incident_id),
      });
    },
  });
};
